import React from "react";
import {
  getWeeksInYearRange,
  calculateWeekWidthsInRange,
  getDaysInYearRange,
  getMonthsInRange
} from "../utils/date";
import { Tooltip } from "@fluentui/react-components";
import { dateOnlyFormatted } from "../../../utils/systemSettings";

export function TimelineHeader({
  viewType,
  subHeader = false,
  displayYearRange,
}) {
  const setting = JSON.parse(localStorage.getItem("settings"));
  const formatDay = (date) => date.getDate().toString().padStart(2, "0");

  const formatDate = (start, end) => {
    const startMonth = start.toLocaleString("en-US", { month: "short" });
    const endMonth = end.toLocaleString("en-US", { month: "short" });
    const startYear = start.getFullYear();
    const endYear = end.getFullYear();
    const formatShortYear = (year) => year.toString().slice(-2);

    if (startYear === endYear && startMonth === endMonth) {
      return `${formatDay(start)}-${formatDay(end)} ${endMonth}`;
    }
    if (startYear === endYear) {
      return `${formatDay(start)} ${startMonth} - ${formatDay(end)} ${endMonth}`;
    }
    return `${formatDay(start)} ${startMonth}, ${formatShortYear(startYear)} - ${formatDay(end)} ${endMonth}, ${formatShortYear(endYear)}`;
  };

  // ---- Weekly ----
  if (viewType === "weekly") {
    const weeks = getWeeksInYearRange(displayYearRange.start, displayYearRange.end);
    return (
      <div className="flex h-full">
        {weeks.map((week, index) => {
          const startDay = week[0];
          const endDay = week[week.length - 1];
          return (
            <div
              key={index}
              className="w-[241px] text-sm border-r border-[#342c82] flex flex-col"
              style={{ height: "60px" }}
            >
              <div className="flex  gap-2 h-[36px] min-h-[36px] justify-center items-center px-2 border-b border-[#342c82]">
                <span className="whitespace-nowrap">Week {index + 1}</span>
                <span className="font-medium whitespace-nowrap">{formatDate(startDay, endDay)}</span>
              </div>
              <div className="flex h-[30px] w-full">
                {week.map((day, i) => (
                  <div
                    key={i}
                    className="relative text-xs flex-1 flex items-baseline justify-center"
                    style={{ minHeight: '100vh', paddingTop: '2px' }}
                  >
                    <div className="absolute right-0 top-0 h-full border-r border-black border-opacity-10 z-1"></div>
                    <Tooltip content={`${dateOnlyFormatted(setting, day)}`} relationship="label">
                      <span>{day.toLocaleDateString("en-US", { weekday: "narrow" })}</span>
                    </Tooltip>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // ---- Monthly subHeader ----
  if (viewType === "monthly" && subHeader) {
    const weeks = getWeeksInYearRange(displayYearRange.start, displayYearRange.end);
    const weekWidths = calculateWeekWidthsInRange(displayYearRange.start, displayYearRange.end);
    return (
      <div className="flex h-full">
        {weeks.map((week, index) => (
          <div 
            key={index} 
            className="relative text-xs flex items-center"
            style={{ width: `${weekWidths[index]}px`, minWidth: `${weekWidths[index]}px`, flexShrink: 0, minHeight: '100vh', alignItems: 'baseline', justifyContent: 'center', paddingTop: '2px' }}
          >
            <div className="absolute right-0 top-0 h-full border-r border-black border-opacity-10 z-1"></div>
            <Tooltip content={`${dateOnlyFormatted(setting, week[0])} - ${dateOnlyFormatted(setting, week[week.length - 1])}`} relationship="label">
              <span className="font-medium px-2 truncate">
                {formatDay(week[0])}-{formatDay(week[week.length - 1])}
              </span>
            </Tooltip>
          </div>
        ))}
      </div>
    );
  }

  // ---- Daily ----
  if (viewType === "daily") {
    const days = getDaysInYearRange(displayYearRange.start, displayYearRange.end);
    return (
      <div className="flex h-full">
        {days.map((day, i) => (
          <div
            key={i}
            className={`${day.toLocaleDateString() === new Date().toLocaleDateString() ? "bg-[#EDECF8]" : ""} gap-1 w-[241px] text-sm flex justify-center items-baseline p-2`}
            style={{
              minHeight: '100vh',
              border: day.toLocaleDateString() === new Date().toLocaleDateString() ? "1px solid rgb(52, 44, 130)" : null,
            }}
          >
            <div className="absolute right-0 top-0 h-full border-r border-[#342c82] border-opacity-50 z-1"></div>
            <span>{day.toLocaleDateString("en-US", { month: "long", day: "2-digit" })}</span>
          </div>
        ))}
      </div>
    );
  }

  if (viewType === "quarter") {
    const months = getMonthsInRange(displayYearRange.start, displayYearRange.end);

    const quarters = [];
    let currentYear = null;
    let quarterNumber = 1;
    let quarterMonths = [];

    for (let i = 0; i < months.length; i++) {
      const month = months[i];

      if (month.year !== currentYear) {
        if (quarterMonths.length > 0) {
          quarters.push({ year: currentYear, quarterNumber, months: quarterMonths });
        }

        currentYear = month.year;
        quarterNumber = 1;
        quarterMonths = [];
      }

      quarterMonths.push(month);

      if (quarterMonths.length === 3) {
        quarters.push({ year: currentYear, quarterNumber, months: quarterMonths });
        quarterNumber++;
        quarterMonths = [];
      }
    }

    if (quarterMonths.length > 0) {
      quarters.push({ year: currentYear, quarterNumber, months: quarterMonths });
    }


    return (
      <>
      <div className="flex h-full">
        {quarters.map((quarter, index) => {
          return (
            <div
              key={index}
              className="w-[501px] text-sm border-r border-[#342c82] flex flex-col"
              style={{ height: "60px" }}
            >
              <div className="flex  gap-2 h-[36px] min-h-[36px] justify-center items-center px-2 border-b border-[#342c82]">
                <span className="whitespace-nowrap font-medium">{`Q${quarter.quarterNumber} - ${quarter.year}`}</span>
              </div>
              <div className="flex h-[30px] w-full">
                {quarter.months.map((month, idx) => (
                  <div
                    key={idx}
                    className="relative font-medium text-sm flex-1 flex items-baseline justify-center w-[167px]"
                    style={{ minHeight: '100vh', paddingTop: '2px' }}
                  >
                    <div className="absolute right-0 top-0 h-full border-r border-black border-opacity-10 z-1"></div>
                      <span>{month.name}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      
      
      </>
      
    );
  }

  // ---- Default monthly ----
  const months = getMonthsInRange(displayYearRange.start, displayYearRange.end);
  return (
    <div className="flex h-full">
      {months.map((month, index) => (
        <div
          key={`${month.year}-${index}`}
          className="w-[241px] font-medium text-sm justify-center border-r border-[#342c82] flex items-center p-2"
        >
          {month.name} {month.year}
        </div>
      ))}
    </div>
  );
}
